import { Block } from "@/backend/models/block";
import { I18n } from "@/i18n/I18n";
import { BlockType } from "@/types/blockTypes";
import { ContainerTypes } from "@/types/containerTypes";
import { BlockTreeObject } from "../store/BlockStore";
import { Container } from "@/backend/models/containers/container";
import { Operation } from "@/backend/models/operation";
import { Section } from "@/backend/models/containers/section";
import { ProcedureInstance } from "@/backend/models/procedureInstances/procedureInstance";
import { ContainerTreeObject } from "../types/navigationTypes";
import { ImplementationFunction } from "@/backend/models/implementationFunction";

export function blockErrorCheck(blockNode: BlockTreeObject, parent: Container | ProcedureInstance | Block | ImplementationFunction, pathname: string, containerLookup: {[key: string]: ContainerTreeObject}, rootId: string) {
  const block = blockNode.item as Block;

  if (blockIsOperationAndParentIsNotStepOrBlock(block, parent)) {
    return I18n.get('operationParentError');
  }

  if (blockIsContainerAndParentIsStep(block, parent)) {
    return I18n.get('containerParentError');
  }

  if (blockIsContainerandParentIsTooDeep(blockNode, parent, containerLookup, rootId)) {
    return I18n.get('nestingError');
  }

  if (blockHasRootStepsAndParentIsNotSection(blockNode, parent)) {
    return I18n.get('containerParentError');
  }

  if (parentAndChildAreBlocksOfDifferentTypes(block, parent)) {
    return I18n.get('blockTypeMismatchError');
  }

  const tabIsScaffold = pathname.includes('scaffold');
  const childrenAreScaffolds = blockNode.children.some(({ item }) => (item as Container | Operation).isScaffold);

  if (tabIsScaffold && !childrenAreScaffolds) {
    return I18n.get('blockNotScaffoldError');
  }

  if (!tabIsScaffold && childrenAreScaffolds) {
    return I18n.get('blockIsScaffoldError');
  }

  return '';
}

const blockIsOperationAndParentIsNotStepOrBlock = (block: Block, parent: Container | ProcedureInstance | Block | ImplementationFunction) => {
  if (block.type === BlockType.Operation) {
    if (parent instanceof ProcedureInstance) {
      return true;
    }

    if (parent instanceof Container && parent.containerType !== ContainerTypes.Scenario) {
      return true;
    }
  }
  return false;
};

const blockIsContainerAndParentIsStep = (block: Block, parent: Container | ProcedureInstance | Block | ImplementationFunction) => {
  return block.type === BlockType.Container && parent instanceof Container && parent.containerType === ContainerTypes.Step;
};

const blockIsContainerandParentIsTooDeep = (blockNode: BlockTreeObject, parent: Container | ProcedureInstance | Block | ImplementationFunction, containerLookup: {[key: string]: ContainerTreeObject}, rootId: string) => {
  const item = blockNode.item as Block;
  if (item.type !== BlockType.Container) return false;
  if (!(parent instanceof Container)) return false;
  const validDepth = isValidDepth(parent.id, containerLookup, rootId, maxDepth(blockNode));
  return !validDepth;
};

const blockHasRootStepsAndParentIsNotSection = (blockNode: BlockTreeObject, parent: Container | ProcedureInstance | Block | ImplementationFunction) => {
  const childrenAreSteps = blockNode.children.some(({ item }) => (item as Container).containerType === ContainerTypes.Step);
  const item = blockNode.item as Block;
  return item.type === BlockType.Container && childrenAreSteps && !(parent instanceof Section);
};

const parentAndChildAreBlocksOfDifferentTypes = (block: Block, parent: Container | ProcedureInstance | Block | ImplementationFunction) => {
  return parent instanceof Block && block.type !== parent.type;
};

const maxDepth  = (node: BlockTreeObject) => {
  const children = node.children.filter(child => child.type === ContainerTypes.Section);
  let max = 0;
  for (let index = 0; index < children.length; index += 1) {
    const depth = maxDepth(children[index]);
    if (depth > max) {
      max = depth;
    }
    if (max === 2) { return max + 1; }
  }
  return max + 1;
};

const isValidDepth = (parentId: string, containerLookup: {[key: string]: ContainerTreeObject}, rootId: string, blockDepth: number) => {
  const maxTargetDepth = 3 - blockDepth;

  let targetContainer = containerLookup[parentId];
  let targetDepth = 0;
  while (targetContainer.parentId !== rootId) {
    targetContainer = containerLookup[targetContainer.parentId];
    targetDepth += 1;
    if (targetDepth > maxTargetDepth) {
      return false;
    }
  }

  return true;
};
